// The three server reads the offline sync engine (BL-2344) polls, over the typed admin client:
// the auction list, one auction's delta (BL-2339) and the reference data with its ETag (BL-2356).
import type { ApiClient, ApiEnvelope } from '@auctogon/core/api/registry';
import { parseResponse } from '@auctogon/core/api/parse-response';
import type { adminRegistry } from '@auctogon/core-admin/api-registry';
import type { AuctionListItem } from '@auctogon/core-admin/schemas/responses/auction';
import { collectEveryAuction } from '../../api-client/auctions';
import type { OfflineReferenceDto } from '@auctogon/core-admin/schemas/responses/offline-reference';
import type { OfflineAuctionDeltaDto, OfflineDeltaCursorDto } from '@auctogon/core-admin/schemas/responses/offline-sync';

/** One delta read: the page, or the auction is gone (404: deleted for good, or not this tenant's). */
export type OfflineDeltaRead = { kind: 'ok'; delta: OfflineAuctionDeltaDto } | { kind: 'gone' };

/** One reference read: unchanged since the stored ETag (304), or a new copy. */
export type OfflineReferenceRead =
  | { kind: 'unchanged' }
  | { kind: 'changed'; reference: OfflineReferenceDto; etag: string | null };

/** The reads the engine needs. Each throws on a failure the engine should report. */
export interface OfflineSyncApi {
  /** @returns the tenant's live (not deleted) auctions. */
  listAuctions(): Promise<AuctionListItem[]>;
  /**
   * @param input.auctionId - the auction.
   * @param input.cursor - the cursor the device holds, or null for a full download.
   * @returns the next page, or `gone`.
   */
  readDelta(input: { auctionId: string; cursor: OfflineDeltaCursorDto | null }): Promise<OfflineDeltaRead>;
  /**
   * @param input.etag - the ETag of the copy the device holds, or null.
   * @returns whether it changed, and the new copy when it did.
   */
  readReference(input: { etag: string | null }): Promise<OfflineReferenceRead>;
}

/**
 * The query the delta route reads the cursor from. No cursor sends nothing, which the server
 * answers with a full download.
 *
 * @param cursor - the device's cursor, or null.
 * @returns the query values.
 */
export function deltaQuery(cursor: OfflineDeltaCursorDto | null): Record<string, string> {
  if (cursor == null) return {};
  return {
    since_sequence: String(cursor.sequence),
    since_at: cursor.at,
    ...(cursor.notes == null ? {} : { notes_since: cursor.notes.since, notes_after: cursor.notes.afterLotId }),
  };
}

/**
 * @param input.api - the typed admin client.
 * @param input.tenantId - the session's active tenant.
 * @returns the engine's reads over that client.
 */
export function createOfflineSyncApi(input: { api: ApiClient<typeof adminRegistry>; tenantId: string }): OfflineSyncApi {
  const { api, tenantId } = input;
  return {
    async listAuctions() {
      return collectEveryAuction(async ({ offset, limit }) => {
        const res = await api.auctions.list.get({ query: { tenantId, offset: String(offset), limit: String(limit) } });
        if (!res.success) throw new Error(`offline sync: auction list failed: ${res.error}`);
        return res.data ?? { items: [], total: 0 };
      });
    },

    async readDelta({ auctionId, cursor }) {
      const { status, body } = await api.offline.auction_delta.get.withStatus({
        params: { id: auctionId },
        query: deltaQuery(cursor),
      });
      if (status === 404) return { kind: 'gone' };
      if (!body.success) throw new Error(`offline sync: delta for auction ${auctionId} failed (${status}): ${body.error}`);
      return { kind: 'ok', delta: body.data };
    },

    async readReference({ etag }) {
      // `.raw()`: the typed envelope reads a bodiless 304 as a failure, and drops the ETag header.
      const res = await api.offline_reference.index.get.raw({
        params: { id: tenantId },
        init: etag == null ? undefined : { headers: { 'If-None-Match': etag } },
      });
      if (res.status === 304) return { kind: 'unchanged' };
      const body = await parseResponse<ApiEnvelope<OfflineReferenceDto>>(res);
      if (!body.success) throw new Error(`offline sync: reference data failed (${res.status}): ${body.error}`);
      return { kind: 'changed', reference: body.data, etag: res.headers.get('ETag') };
    },
  };
}
